import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ChevronRight } from "lucide-react";
import FutureSessionMeter from "@/components/FutureSessionMeter";
import { useFutureSessionOverview } from "@/hooks/useScheduleOptions";

interface Props {
  onOpenClass: (classId: string) => void;
}

/**
 * Operational overview of every non-hidden schedule option across all classes.
 * Sorted goal-reached → closest-to-goal → highest interest. Clicking a row
 * opens that class directly to the Potential Future Sessions manager.
 */
export default function FutureSessionInterestOverview({ onOpenClass }: Props) {
  const { rows, loading } = useFutureSessionOverview();
  if (loading || rows.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary/70" /> Future Session Interest
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((row) => {
          const reached = row.interest_threshold > 0 && row.interest_count >= row.interest_threshold;
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => onOpenClass(row.class_id)}
              className="w-full text-left rounded-md border border-border/70 bg-card hover:bg-accent/40 transition px-3 py-2.5 flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground truncate">{row.class_title}</span>
                  <span className="text-xs text-muted-foreground">— {row.display_label}</span>
                  {reached && (
                    <Badge className="bg-emerald-100 text-emerald-900 border border-emerald-300 text-[10px]">
                      Goal Reached
                    </Badge>
                  )}
                </div>
                <div className="mt-1.5">
                  <FutureSessionMeter
                    count={row.interest_count}
                    threshold={row.interest_threshold}
                    compact
                  />
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
}
